import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';

// layout 
import AppLayout from '../../../layouts/AppLayout/AppLayout';

// components 
import { NioSection, NioCard, NioBadge, NioButton, NioIcon, NioToaster, ProviderMap } from '../../../components';

// API
import api from '../../../utils/api';
import { useTranslation } from '../../../i18n/I18nProvider';
import { translateServiceLabel } from '../../../i18n/translateService';

function getLoggedInUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Geocode city + region (Tunisia) via Nominatim - 1 request per second to respect usage policy
async function geocodeCityRegion(city, region) {
  if (!city || !region) return null;
  const query = encodeURIComponent(`${city}, ${region}, Tunisia`);
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1`,
      { headers: { 'Accept-Language': 'fr' } }
    );
    const data = await res.json();
    if (data && data[0]) {
      return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
    }
  } catch (e) {
    console.warn('Geocoding failed for', city, region, e);
  }
  return null;
}

function ServiceProviders() {
  const { t, language } = useTranslation();
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [providersWithCoords, setProvidersWithCoords] = useState([]); // providers enriched with lat/lng for map
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(getLoggedInUser);
  const [reviewStatsByProvider, setReviewStatsByProvider] = useState({});
  const geocodeDoneRef = useRef(false);

  useEffect(() => {
    setLoggedInUser(getLoggedInUser());
  }, []);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setLoading(true);
        setError('');
        geocodeDoneRef.current = false;
        
        const decodedServiceId = serviceId ? decodeURIComponent(serviceId) : serviceId;
        const response = await api.getProvidersByService(decodedServiceId);
        
        if (response.success) {
          const list = Array.isArray(response.data) ? response.data : [];
          setProviders(list);
          setService(response.service || null);
          setProvidersWithCoords(list);
        } else {
          setError(response.message || t('serviceProviders.loadFailed'));
          setShowError(true);
        }
      } catch (err) {
        console.error('Error fetching providers:', err);
        setError(err.message || t('serviceProviders.loadFailed'));
        setShowError(true);
        setTimeout(() => setShowError(false), 5000);
      } finally {
        setLoading(false);
      }
    };

    if (serviceId) {
      fetchProviders();
    } else {
      setLoading(false);
      setError(t('serviceProviders.noServiceSelected'));
      setProviders([]);
      setService(null);
    }
  }, [serviceId]);

  // Enrich providers without lat/lng using city+region geocoding (for map display)
  useEffect(() => {
    if (!providers.length || geocodeDoneRef.current) return;
    const hasCoords = (p) => p && p.latitude != null && p.longitude != null && !isNaN(parseFloat(p.latitude)) && !isNaN(parseFloat(p.longitude));
    const needGeocode = providers.filter((p) => !hasCoords(p) && p.city && p.region);
    if (needGeocode.length === 0) {
      geocodeDoneRef.current = true;
      return;
    }
    const uniqueKeys = [...new Set(needGeocode.map((p) => `${p.city}|${p.region}`))];
    const cache = {};
    const run = async () => {
      for (let i = 0; i < uniqueKeys.length; i++) {
        const key = uniqueKeys[i];
        const [city, region] = key.split('|');
        const coords = await geocodeCityRegion(city, region);
        if (coords) cache[key] = coords;
        if (i < uniqueKeys.length - 1) await new Promise((r) => setTimeout(r, 1100));
      }
      geocodeDoneRef.current = true;
      setProvidersWithCoords((prev) =>
        prev.map((p) => {
          if (hasCoords(p)) return p;
          const key = p.city && p.region ? `${p.city}|${p.region}` : null;
          const c = key ? cache[key] : null;
          if (c) return { ...p, latitude: c.lat, longitude: c.lng };
          return p;
        })
      );
    };
    run();
  }, [providers]);

  useEffect(() => {
    const loadReviewStats = async () => {
      const ids = providers.map((p) => p._id).filter(Boolean);
      if (!ids.length) {
        setReviewStatsByProvider({});
        return;
      }
      try {
        const res = await api.getProviderReviewsBatch(ids);
        if (res?.success) {
          setReviewStatsByProvider(res.statsByProvider || {});
        }
      } catch {
        setReviewStatsByProvider({});
      }
    };
    loadReviewStats();
  }, [providers]);

  const renderProviderRating = (providerId) => {
    const stats = reviewStatsByProvider[String(providerId)];
    if (!stats || !stats.count) return null;
    return (
      <div className="d-flex align-items-center gap-2 mb-2">
        <span style={{ color: '#f59e0b', display: 'inline-flex' }}><NioIcon name="star-fill" /></span>
        <span className="small fw-semibold">{stats.averageRating}</span>
        <span className="small text-muted">({t('serviceProviders.reviewsCount', { count: stats.count })})</span>
      </div>
    );
  };

  const toasterMessages = [];
  if (showError) {
    toasterMessages.push({ id: 'error', type: 'error', message: error });
  }

  const getTextPreview = (value, maxLines = 3) => {
    if (value == null) return '';
    const str = String(value);
    const lines = str
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    return lines.length ? lines.slice(0, maxLines).join('\n') : '';
  };

  const getZoneIntervention = (p) => {
    if (!p) return '';
    return (
      p.coverageGuarantees ||
      p.location ||
      [p.city, p.region].filter(Boolean).join(', ')
    );
  };

  const getPortfolioPhotos = (p) => {
    if (!p) return [];
    const photos = Array.isArray(p.portfolioPhotos) ? p.portfolioPhotos : [];
    return photos.filter((x) => typeof x === 'string' && x.trim());
  };

  return (
    <AppLayout title={t('serviceProviders.pageTitle')} rootClass="layout-1">
      <NioToaster messages={toasterMessages} />

      <NioSection className="overflow-hidden pt-120 pt-lg-160" masks={["blur-1 left center"]}>
        <NioSection.Content>
          <Row className="justify-content-center text-center mb-5">
            <Col lg={8} xl={6}>
              <div className="nk-section-head">
                <span className="d-inline-block fs-14 text-uppercase text-primary fw-semibold mb-2">
                  {service ? (service.titles?.[language] || translateServiceLabel(t, service.title)) : (serviceId ? `ID: ${serviceId}` : t('serviceProviders.pageTitle'))}
                </span>
                <h2>
                  {service ? t('serviceProviders.forService', { name: (service.titles?.[language] || translateServiceLabel(t, service.title)) }) : t('serviceProviders.pageTitle')}
                </h2>
                <p className="fs-20">
                  {service?.descriptions?.[language] || service?.description || t('serviceProviders.browseDesc')}
                </p>
                {!service && serviceId && (
                  <p className="text-muted small mt-2">
                    {t('serviceProviders.loadingServiceInfo')}
                  </p>
                )}
                {service && service.tags && service.tags.length > 0 && (
                  <div className="d-flex justify-content-center gap-2 flex-wrap mt-3">
                    {Array.isArray(service.tags) 
                      ? service.tags.map((tag, idx) => (
                          <NioBadge key={idx} rounded className="text-bg-primary-soft" label={tag} />
                        ))
                      : <NioBadge rounded className="text-bg-primary-soft" label={service.tags} />
                    }
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {!serviceId ? (
            <Row className="justify-content-center">
              <Col lg={8}>
                <NioCard className="text-center py-5">
                  <NioCard.Body>
                    <NioIcon name="info-circle" className="fs-48 text-muted mb-3" />
                    <h4 className="mb-3">{t('serviceProviders.noServiceTitle')}</h4>
                    <p className="text-muted mb-4">
                      {t('serviceProviders.noServiceBody')}
                    </p>
                    <Link to="/services">
                      <NioButton className="btn-primary" label={t('common.viewServices')} />
                    </Link>
                  </NioCard.Body>
                </NioCard>
              </Col>
            </Row>
          ) : loading ? (
            <Row className="justify-content-center">
              <Col lg={8}>
                <div className="text-center py-5">
                  <p className="fs-18">{t('serviceProviders.loading')}</p>
                  <p className="text-muted small mt-2">Service : {decodeURIComponent(serviceId)}</p>
                </div>
              </Col>
            </Row>
          ) : providers.length > 0 ? (
            <>
              <Row className="mb-4">
                <Col>
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <p className="mb-0 text-muted">
                      <strong>{providers.length}</strong>{' '}
                      {t('serviceProviders.found', { count: providers.length })}
                    </p>
                    <Link to="/services" className="text-primary fw-medium">
                      {t('serviceProviders.backToServicesLink')}
                    </Link>
                  </div>
                </Col>
              </Row>

              {/* Map Section - Localisation des prestataires */}
              <Row className="mb-5">
                <Col>
                  <NioCard className="shadow-sm border-0 overflow-hidden">
                    <NioCard.Body className="p-0">
                      <div className="p-4 pb-2">
                        <h5 className="mb-1">
                          <NioIcon name="geo-alt" className="text-primary me-2" />
                          {t('serviceProviders.mapTitle')}
                        </h5>
                        <p className="text-muted small mb-0">{t('serviceProviders.mapHint')}</p>
                      </div>
                      <div className="px-4 pb-4">
                        <ProviderMap 
                          providers={providersWithCoords} 
                          height="450px"
                          zoom={providersWithCoords.length === 1 ? 12 : 8}
                        />
                      </div>
                    </NioCard.Body>
                  </NioCard>
                </Col>
              </Row>

              {/* Liste des prestataires avec leurs informations */}
              <Row className="mb-3">
                <Col>
                  <h5 className="mb-0">
                    <NioIcon name="people" className="text-primary me-2" />
                   {t('serviceProviders.contactSectionTitle')}
                  </h5>
                  <p className="text-muted small mt-1 mb-0">{t('serviceProviders.contactSectionDesc')}</p>
                </Col>
              </Row>
              <Row className="gy-4">
                {providers.map((provider, idx) => {
                  const isCurrentUser = loggedInUser && (
                    (provider._id && loggedInUser.id && String(provider._id) === String(loggedInUser.id)) ||
                    provider.email === loggedInUser.email
                  );
                  return (
                  <Col md={6} lg={4} key={provider._id || idx}>
                    <NioCard className={`h-100 shadow-sm ${isCurrentUser ? 'border-primary border-2' : ''}`}>
                      <NioCard.Body className="p-4 position-relative">
                        {isCurrentUser && (
                          <NioBadge className="position-absolute top-0 end-0 m-2 text-bg-primary">
                            {t('serviceProviders.itsYou')}
                          </NioBadge>
                        )}
                        <div className="d-flex align-items-start mb-3">
                          <div className="flex-shrink-0">
                            <div 
                              className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                              style={{ width: '60px', height: '60px', fontSize: '24px' }}
                            >
                              {provider.name?.charAt(0)?.toUpperCase() || 'P'}
                            </div>
                          </div>
                          <div className="flex-grow-1 ms-3">
                            <h5 className="mb-1">
                              {provider.name} {provider.lastName}
                            </h5>
                            {provider.businessName && (
                              <p className="text-muted small mb-0">{provider.businessName}</p>
                            )}
                            {renderProviderRating(provider._id)}
                          </div>
                        </div>

                        {provider.description && (
                          <p className="mb-3 text-muted small" style={{ 
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}>
                            {provider.description}
                          </p>
                        )}

                        <div className="mb-3">
                          {provider.region && provider.city && (
                            <div className="d-flex align-items-center mb-2">
                              <NioIcon name="geo-alt" className="text-primary me-2" />
                              <span className="small text-muted">
                                {provider.city}, {provider.region}
                              </span>
                            </div>
                          )}
                          {provider.phone && (
                            <div className="d-flex align-items-center mb-2">
                              <NioIcon name="telephone" className="text-primary me-2" />
                              <span className="small text-muted">{provider.phone}</span>
                            </div>
                          )}
                          {provider.email && (
                            <div className="d-flex align-items-center">
                              <NioIcon name="mail" className="text-primary me-2" />
                              <span className="small text-muted">{provider.email}</span>
                            </div>
                          )}
                        </div>

                        <div className="d-flex flex-wrap gap-2 mb-3">
                          {provider.experience && (
                            <NioBadge 
                              rounded 
                              className="text-bg-primary-soft" 
                              label={t('serviceProviders.yearsExp', { years: provider.experience })} 
                            />
                          )}
                          {provider.qualifications && (
                            <NioBadge 
                              rounded 
                              className="text-bg-success-soft" 
                              label={provider.qualifications} 
                            />
                          )}
                        </div>

                        <div className="mb-3">
                          {provider.schedule && (
                            <div className="mb-2">
                              <div className="text-muted small text-uppercase mb-1">
                                {t('serviceProviders.availability')}
                              </div>
                              <div
                                className="small"
                                style={{ whiteSpace: 'pre-wrap', maxHeight: 54, overflow: 'hidden' }}
                              >
                                {getTextPreview(provider.schedule, 3) || '—'}
                              </div>
                            </div>
                          )}

                          {getZoneIntervention(provider) && (
                            <div className="mb-2">
                              <div className="text-muted small text-uppercase mb-1">
                                {t('serviceProviders.zone')}
                              </div>
                              <div className="small text-muted">
                                {getZoneIntervention(provider)}
                              </div>
                            </div>
                          )}

                          {provider.pricing && (
                            <div className="mb-2">
                              <div className="text-muted small text-uppercase mb-1">
                                {t('serviceProviders.pricing')}
                              </div>
                              <div className="small text-muted">{provider.pricing}</div>
                            </div>
                          )}

                          {provider.paymentMethod && (
                            <div className="mb-2">
                              <div className="text-muted small text-uppercase mb-1">
                                {t('serviceProviders.paymentMethod')}
                              </div>
                              <div className="small text-muted">{provider.paymentMethod}</div>
                            </div>
                          )}

                          {getPortfolioPhotos(provider).length > 0 && (
                            <div className="mb-2">
                              <div className="text-muted small text-uppercase mb-1">
                                {t('serviceProviders.portfolio')}
                              </div>
                              <div className="d-flex gap-2 flex-wrap">
                                {getPortfolioPhotos(provider)
                                  .slice(0, 3)
                                  .map((photo, photoIdx) => (
                                    <a
                                      // eslint-disable-next-line react/no-array-index-key
                                      key={photoIdx}
                                      href={photo}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <img
                                        src={photo}
                                        alt={`Portfolio ${photoIdx + 1}`}
                                        style={{
                                          width: 48,
                                          height: 48,
                                          objectFit: 'cover',
                                          borderRadius: 6,
                                          border: '1px solid rgba(0,0,0,0.08)',
                                        }}
                                      />
                                    </a>
                                  ))}
                                {getPortfolioPhotos(provider).length > 3 && (
                                  <div className="small text-muted d-flex align-items-center">
                                    {t('serviceProviders.portfolioMore', { count: getPortfolioPhotos(provider).length - 3 })}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="d-flex gap-2 justify-content-center flex-wrap">
                          <Link
                            to={`/providers/${provider._id}`}
                            state={{
                              provider,
                              fromPath: `/service/${serviceId}/providers`,
                              serviceId,
                              serviceTitle: service?.title,
                            }}
                            className="btn btn-sm btn-outline-secondary"
                          >
                            {t('serviceProviders.contactProvider')}
                          </Link>
                          {!isCurrentUser ? (
                            <Link
                              to={`/providers/${provider._id}`}
                              state={{
                                provider,
                                fromPath: `/service/${serviceId}/providers`,
                                serviceId,
                                serviceTitle: service?.title,
                              }}
                              className="btn btn-sm btn-outline-primary"
                            >
                              {t('serviceProviders.leaveReview')}
                            </Link>
                          ) : null}
                        </div>
                      </NioCard.Body>
                    </NioCard>
                  </Col>
                );
                })}
              </Row>
            </>
          ) : showError && error ? (
            <Row className="justify-content-center">
              <Col lg={8}>
                <NioCard className="text-center py-5 border-warning">
                  <NioCard.Body>
                    <NioIcon name="exclamation-triangle" className="fs-48 text-warning mb-3" />
                    <h4 className="mb-3">{t('serviceProviders.loadErrorTitle')}</h4>
                    <p className="text-muted mb-4">{error}</p>
                    <Link to="/services" className="me-2">
                      <NioButton className="btn-outline-primary" label={t('serviceProviders.backToServices')} />
                    </Link>
                    <NioButton className="btn-primary" label={t('common.retry')} onClick={() => window.location.reload()} />
                  </NioCard.Body>
                </NioCard>
              </Col>
            </Row>
          ) : (
            <Row className="justify-content-center">
              <Col lg={8}>
                <NioCard className="text-center py-5">
                  <NioCard.Body>
                    <NioIcon name="inbox" className="fs-48 text-muted mb-3" />
                    <h4 className="mb-3">{t('serviceProviders.noneTitle')}</h4>
                    <p className="text-muted mb-4">
                      {t('serviceProviders.noneBody')}
                    </p>
                  </NioCard.Body>
                </NioCard>
              </Col>
            </Row>
          )}
        </NioSection.Content>
      </NioSection>
    </AppLayout>
  );
}

export default ServiceProviders;
